import {
    fetchPermissions,
    fetchSystemSettings,
    updateOrganisationLanguage,
    updateOrganisationName,
    updatePermission,
    updateSecret,
    fetchReleaseNotesRecipients,
} from '../services/systemSettingsService';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async Thunks
export const requestSystemSettings = createAsyncThunk(
    'systemSettings/requestSystemSettings',
    async () => {
        const response = await fetchSystemSettings();
        return response.data;
    }
);

export const changeOrganisationName = createAsyncThunk(
    'systemSettings/changeOrganisationName',
    async (name, { rejectWithValue }) => {
        try {
            const { data } = await updateOrganisationName({ organisationName: name });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const changeOrganisationLanguage = createAsyncThunk(
    'systemSettings/changeOrganisationLanguage',
    async (languageCode, { rejectWithValue }) => {
        try {
            const { data } = await updateOrganisationLanguage({ language: languageCode });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const requestPermissions = createAsyncThunk(
    'systemSettings/requestPermissions',
    async () => {
        const response = await fetchPermissions();
        return response.data;
    }
);

export const changePermission = createAsyncThunk(
    'systemSettings/changePermission',
    async ({ name, enabled, value }, { rejectWithValue }) => {
        try {
            const { data } = await updatePermission({ name, enabled, value })
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const rotateSecret = createAsyncThunk(
    'systemSettings/rotateSecret',
    async (currentSecret, { rejectWithValue }) => {
        try {
            const { data } = await updateSecret(currentSecret);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const requestReleaseNotesRecipients = createAsyncThunk(
    'systemSettings/requestReleaseNotesRecipients',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchReleaseNotesRecipients();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Slice
const systemSettingsSlice = createSlice({
    name: 'systemSettings',
    initialState: {
        organisationSettings: {},
        isFetchingSettings: false,
        permissions: [],
        permissionGroups: [],
        releaseNotesRecipients: [],
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(requestSystemSettings.pending, (state) => {
                state.isFetchingSettings = true;
            })
            .addCase(requestSystemSettings.fulfilled, (state, action) => {
                state.organisationSettings = action.payload;
                state.isFetchingSettings = false;
            })
            .addCase(requestSystemSettings.rejected, (state, action) => {
                console.error('Failed to fetch system settings:', action.error);
                state.isFetchingSettings = false;
            })
            .addCase(requestPermissions.pending, (state) => {
                state.isFetchingSettings = true;
            })
            .addCase(requestPermissions.fulfilled, (state, action) => {
                state.permissions = action.payload.permissions;
                state.permissionGroups = action.payload.permissionGroups;
                state.isFetchingSettings = false;
            })
            .addCase(requestPermissions.rejected, (state, action) => {
                console.error('Failed to fetch permissions:', action.error);
                state.isFetchingSettings = false;
            })
            .addCase(changeOrganisationName.pending, (state) => {
                state.isFetchingSettings = true;
            })
            .addCase(changeOrganisationName.fulfilled, (state, action) => {
                state.organisationSettings.organisationName = action.payload.organisationName;
                state.isFetchingSettings = false;
            })
            .addCase(changeOrganisationName.rejected, (state, action) => {
                console.error('Failed to change organisation name:', action.error);
                state.isFetchingSettings = false;
            })
            .addCase(changeOrganisationLanguage.pending, (state) => {
                state.isFetchingSettings = true;
            })
            .addCase(changeOrganisationLanguage.fulfilled, (state, action) => {
                state.organisationSettings.organisationLanguage = action.payload.organisationLanguage;
                window.CHAMP.locale.data.map = action.payload.localeData.translations;
                window.CHAMP.locale.data.locale = action.payload.localeData.code;
                state.isFetchingSettings = false;
                window.location.reload();
            })
            .addCase(changeOrganisationLanguage.rejected, (state, action) => {
                console.error('Failed to change organisation language:', action.error);
                state.isFetchingSettings = false;
            })
            .addCase(rotateSecret.pending, (state) => {
                state.isFetchingSettings = true;
            })
            .addCase(rotateSecret.fulfilled, (state, action) => {
                state.organisationSettings.secret = action.payload.secret;
                state.isFetchingSettings = false;
            })
            .addCase(rotateSecret.rejected, (state, action) => {
                console.error('Failed to rotate secret:', action.error);
                state.isFetchingSettings = false;
            })
            .addCase(changePermission.pending, (state) => {
                state.isFetchingSettings = true;
            })
            .addCase(changePermission.fulfilled, (state, action) => {
                state.permissions = action.payload.permissions;
                state.permissionGroups = action.payload.permissionGroups;
                state.isFetchingSettings = false;
            })
            .addCase(changePermission.rejected, (state, action) => {
                console.error('Failed to change permission:', action.error);
                state.isFetchingSettings = false;
            })
            .addCase(requestReleaseNotesRecipients.pending, (state) => {
                state.isFetchingSettings = true;
            })
            .addCase(requestReleaseNotesRecipients.fulfilled, (state, action) => {
                state.releaseNotesRecipients = action.payload;
                state.isFetchingSettings = false;
            })
            .addCase(requestReleaseNotesRecipients.rejected, (state, action) => {
                console.error('Failed to fetch release notes recipients:', action.error);
                state.isFetchingSettings = false;
            });
    },
});

export default systemSettingsSlice.reducer;
export const { actions: systemSettingsActions } = systemSettingsSlice;
